import { Decimal } from '@snap-types/types/decimal';
import { getQuotePriceOrThrow } from '@ui/features/quote-builder/state/utilities/action-guards';
import { isEditableQuotePriceField } from '@ui/features/quote-builder/state/utilities/is-editable-quote-price-field';
import { z } from 'zod/v4';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { parseNumberInput, validateDecimalPrecision } from './number-input';

type ValidateGrossMarginParams = {
  grossMargin: string | Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;
  field: 'grossMargin' | 'baseGrossMargin';
};

const baseValidator = z
  .number({
    error: 'Gross margin must be a number',
  })
  .lt(100, 'Gross margin must be less than 100%');

export function validateGrossMargin(
  state: WritableQuoteBuilderStateType,
  {
    grossMargin,
    quoteLineUuid,
    quotePriceUuid,
    field,
  }: ValidateGrossMarginParams,
) {
  const quotePrice = getQuotePriceOrThrow(state, quoteLineUuid, quotePriceUuid);
  const isEditable = isEditableQuotePriceField(quotePrice.priceStrategy, field);

  let validator = baseValidator;
  if (isEditable) {
    validator = baseValidator.gt(
      -100,
      'Gross margin must be greater than -100%',
    );
  }

  const grossMarginString = grossMargin.toString();

  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field,
  });

  const parsed = parseNumberInput({
    value: grossMarginString,
    requiredMessage: 'Gross margin is required',
    invalidMessage: 'Gross margin must be a number',
  });

  if (parsed.error) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid gross margin',
      description: parsed.error,
      severity: 'error',
      type: 'invalid',
      field,
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field,
        }),
      },
    });
  }

  const precisionError = validateDecimalPrecision({
    value: grossMarginString,
    maxDecimalPlaces: 2,
    decimalPlacesMessage: 'Gross margin cannot have more than 2 decimal places',
  });

  if (precisionError) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid gross margin',
      description: precisionError,
      severity: 'error',
      type: 'invalid',
      field,
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field,
        }),
      },
    });
  }

  const result = validator.safeParse(parsed.parsed);

  if (!result.success) {
    const error = z.flattenError(result.error).formErrors[0];

    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid gross margin',
      description: error,
      severity: 'error',
      type: 'invalid',
      field,
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field,
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  return new Decimal(result.data);
}
